<template>
  <div class="drag-and-drop-file">
    <v-card
        @click="dragAndDropClick"
        @drop.prevent="onDrop($event)"
        @dragover.prevent="dragover = true"
        @dragenter.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
        :class="'drag-and-drop ' + (dragover ? 'grey lighten-2' : '') + ' ' + errorClass"
        elevation="0"
        :style="customHeight ? `height: ${customHeight}!important;` : ''"
        :disabled="readOnly"
    >
      <div v-if="srcType === 'application/pdf' && previewMode && !isUploading" class="pdf-preview-wrapper">
        <div v-if="$vuetify.breakpoint.smAndUp"  class="drag-and-drop-bg" >
          <pdf-viewer :src="srcToPreview" @downloadFile="downloadBlob" />
        </div>
        <div v-else class="no-preview">
          <h6 class="greyTint--text font-weight-bold">{{ $t('components:drag-and-drop-file.no-preview') }}</h6>
          <v-btn fab color="primary" elevation="2" @click.native.stop="downloadFile">
            <v-icon>file_download</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-else-if="previewMode && !isUploading" class="drag-and-drop-bg" :style="customPreviewHeightWidth">
        <v-img :height="previewHeight ? previewHeight : '100%'" contain :src="srcToPreview"/>
      </div>

      <div v-if="isUploading" class="progress-bar">
        <v-progress-linear
            v-model="progress"
            color="primary"
            value="45"
            height="25"
        >
          <strong>{{ progress }} %</strong>
        </v-progress-linear>
      </div>

    <v-card-text class="my-auto mx-1 mx-sm-3 mx-md-5" v-show="!previewMode && !isUploading">
        <v-row class="d-flex flex-column" dense align="center" justify="center">
          <v-icon :class="[dragover ? 'mt-2, mb-6' : 'mt-5']" size="60">
            cloud_upload
          </v-icon>
          <p>
            {{ $t('components:drag-and-drop-file.title') }}
          </p>
        </v-row>

        <v-file-input
            v-show="file"
            ref="fileInput"
            accept="image/*, application/pdf"
            :label="$t('components:common.filename')"
            show-size
            v-model="file"
            prepend-icon=""
            truncate-length="50"
            :rules="getRules"
            @change="onSelect"
        />
      </v-card-text>
    <div v-show="previewMode && !isUploading && !viewOnly" class="button-wrapper">
      <v-btn fab color="error" elevation="2" small  @click.native.stop="clear">
        <v-icon>delete</v-icon>
      </v-btn>
    </div>
    </v-card>
    <div class="error--text" v-if="errorClass.length > 0">{{ $t('utils:rules.required') }}</div>
  </div>
</template>
<script>

import {ruleRequired, ruleMaxFileSize} from "@/modules/core/services/rules";
import PdfViewer from './PdfViewer';

export default {
  name: 'DragAndDropFile',
  components: {
    PdfViewer,
  },
  props: {
    customHeight: String,
    previewHeight: String,
    previewWidth: String,
    viewOnly: Boolean,
    readOnly: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      notRequired: false,
      dragover: false,
      file: null,
      srcToPreview: null,
      srcType: null,
      document: null,
      previewMode: false,
      progress: 0,
      isUploading: false,
      errorClass: ''
    };
  },
  computed: {
    fileSizeLimit() {
      return process.env.VUE_APP_UPLOAD_SIZE_DOCUMENT;
    },
    getRules() {
      if (this.notRequired) {
        return [ ruleMaxFileSize(this.fileSizeLimit,this.$t('utils:rules.filesize', {size : '20 MB'})) ]
      } else {
        return [
          ruleRequired(this.$t('utils:rules.requiredFile')),
          ruleMaxFileSize(this.fileSizeLimit,this.$t('utils:rules.filesize', {size : '20 MB'}))
        ]
      }
    },
    customPreviewHeightWidth() {
      if (this.previewHeight && this.previewWidth) {
        return `height: ${this.previewHeight}; width: ${this.previewWidth}; left: 0; right: 0; top: 0; bottom: 0; margin: auto`
      } else if (this.previewWidth) {
        return `width: ${this.previewWidth}; height: auto; left: 0; right: 0; top: 0; bottom: 0; margin: auto`
      } else if (this.previewHeight) {
        return `height: ${this.previewHeight}; width: auto; left: 0; right: 0; top: 0; bottom: 0; margin: auto`
      } else {
        return '';
      }
    }
  },
  methods: {
    ruleRequired,
    ruleMaxFileSize,
    downloadFile() {
      this.$emit("downloadFile", this.document);
    },
    downloadBlob() {
      this.$emit("downloadBlob", this.document);
    },
    removeFile() {
      this.progress = 0;
      this.$emit("removeFile", this.document.id);
    },
    clear() {
      this.file = null;
      this.setPreviewSrc(null);
      this.progress = 0;
      this.errorClass = '';
    },
    onSelect(e) {
      if (this.viewOnly) return;
      if (!e) {
        this.setPreviewSrc(null)
      }
      this.isUploading = true;
      this.progress = 0;

      this.$emit('fileChange', e)
    },

    uploadAsUrl(file) {
      let reader = new FileReader();
      let docuToParse = {};

      reader.readAsDataURL(file);
      reader.onloadend = () => {
        docuToParse = {'name': '', 'src': reader.result, 'fileType':file.type};
        this.setPreviewSrc(docuToParse);
        this.isUploading = false;
      }
    },

    onDrop(e) {
      if (this.viewOnly) return;
      if (!e) {
        this.setPreviewSrc(null)
      }

      this.dragover = false;
      this.file = e.dataTransfer.files[0]

      this.isUploading = true;
      this.progress = 0;

      this.$emit('fileChange', this.file)
    },

    setPreviewSrc(document) {
      this.document = document;

      if (document) {
        this.srcToPreview = document.src;
        this.srcType = document.fileType;
      } else {
        this.srcToPreview = null;
        this.srcType = null;
      }

      if (this.srcToPreview && this.srcType) {
        this.previewMode = true;
      } else {
        this.previewMode = false;
      }
    },
    dragAndDropClick() {
      if (this.viewOnly) return;
      if (!this.srcToPreview) {
        this.$refs.fileInput.$refs.input.click();
      }
    },
    isMandatoryFilledOut() {
      let result = true;
      this.errorClass = '';

      if (!this.file && !this.srcToPreview) {
        result = false;
        this.errorClass = 'border-error';
      }

      return result
    }
  }
}
</script>
<style lang="scss">
.drag-and-drop-file {
  .progress-bar {
    width: 100%;
    height: 100%;
    position: relative;

    .v-progress-linear {
      width: 90%;
      margin: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);

      .v-progress-linear__content {
        color: white;
      }
    }
  }
  // temp css for drag and drop dummy
  .drag-and-drop {
    height: 100% !important;
    border: 2px dashed grey !important;
    display: flex;

    .drag-and-drop-bg {
      position: absolute;
      height: 100%;
      width: 100%;
      margin: 0 auto;
    }

    .v-card__text {
      z-index: 0;
      background: rgba(255, 255, 255, 0.95);
      box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    }

    .button-wrapper {
      position: absolute;
      bottom: 10px;
      left: 10px;

      .v-btn--round {
        display: block;
        margin: auto;
      }
    }

    &.border-error {
      border: 1px solid #ff4081;
    }
  }
}

@media screen and (max-width: 600px) {
  .drag-and-drop {
    height: 450px !important;

    .button-wrapper {
      bottom: 20px;
    }

    .pdf-preview-wrapper {
      width: 100%;

      .no-preview {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>